import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Lock, Printer, Check, ChevronDown, ChevronUp } from 'lucide-react';
import { cacheData, loadWithSavedCopy } from '../utils/offlineCache';

const API_URL = process.env.REACT_APP_API_URL || '';

// The three ways a gig decides which songs the audience may request (curation spec §3.2)
const MODES = [
  { key: 'curated',    label: 'Curated',     blurb: 'Gig-ready songs only.' },
  { key: 'relaxed',    label: 'Relaxed',     blurb: 'Gig-ready and rusty songs. Rusty ones are labelled 🤞.' },
  { key: 'handpicked', label: 'Hand-picked', blurb: 'Only the songs you tick.', paid: true },
];

const byReadyThenTitle = (a, b) =>
  (a.readiness === 'rusty') - (b.readiness === 'rusty') || a.title.localeCompare(b.title);

// The Set List Builder's "Request list" panel: the mode, a live count and preview of what the
// audience will see, the hand-pick checklist, and the tear-off sheet's print button
// (only when the set list has a request block). Performers see it but can't change it.
function RequestListPanel({ gig, allSongs, usedSongIds, hasRequestBlock, canEdit, subscriptionTier, theme, isMobile }) {
  const [data, setData] = useState(null);
  const hasData = useRef(false);
  const [offline, setOffline] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showList, setShowList] = useState(false);
  const [draft, setDraft] = useState(null);   // Set of picked ids while hand-picking, null when unchanged
  const [search, setSearch] = useState('');
  const [genre, setGenre] = useState('');

  const paid = subscriptionTier === 'pro' || subscriptionTier === 'band';
  const usedKey = [...usedSongIds].sort((a, b) => a - b).join(',');

  // Refetch when the set list changes: songs placed in a set leave the request list
  useEffect(() => {
    let cancelled = false;
    const show = d => { if (!cancelled) { hasData.current = true; setData(d); } };
    loadWithSavedCopy(`requestlist:${gig.id}`, async () => {
      const res = await axios.get(`${API_URL}/api/gigs/${gig.id}/request-list`);
      return res.data;
    }, show, { emptyPage: !hasData.current })
      .then(cachedAt => { if (!cancelled) setOffline(!!cachedAt); })
      .catch(() => { if (!cancelled) setOffline(true); });
    return () => { cancelled = true; };
  }, [gig.id, usedKey]);

  // Songs that could be on the list: active, not on the set list, not "not ready"
  const candidates = useMemo(() => {
    const used = new Set(usedSongIds);
    return allSongs.filter(s => s.is_active && !used.has(s.id) && s.readiness !== 'not_ready').sort(byReadyThenTitle);
  }, [allSongs, usedSongIds]);

  const genres = useMemo(() => [...new Set(candidates.map(s => s.genre).filter(Boolean))].sort(), [candidates]);

  if (!data) {
    return <div style={{ padding: '1rem', color: theme.textMuted, fontSize: '0.9rem' }}>{offline ? 'The request list needs signal.' : 'Loading request list…'}</div>;
  }

  const save = async (body, message) => {
    setSaving(true);
    try {
      const res = await axios.patch(`${API_URL}/api/gigs/${gig.id}/request-list`, body);
      setData(res.data);
      cacheData(`requestlist:${gig.id}`, res.data);
      setDraft(null);
      if (message) toast.success(message);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Couldn\'t save the request list');
    } finally {
      setSaving(false);
    }
  };

  const chooseMode = (key) => {
    if (!canEdit || saving || key === data.mode) return;
    if (key === 'handpicked' && !paid) {
      toast.error('Hand-picked request lists are on Pro and Band');
      return;
    }
    const label = MODES.find(m => m.key === key).label;
    // The first time a gig is hand-picked, start from its gig-ready songs
    if (key === 'handpicked' && data.picks.length === 0) {
      save({ mode: key, picks: candidates.filter(s => s.readiness === 'ready').map(s => s.id) }, `${label}: untick any songs you don't want`);
    } else {
      save({ mode: key }, `Request list: ${label}`);
    }
  };

  const handpicking = data.effective_mode === 'handpicked';
  const picked = draft || new Set(data.picks);
  const togglePick = (id) => {
    const next = new Set(picked);
    next.has(id) ? next.delete(id) : next.add(id);
    setDraft(next);
  };

  // The live count, and what's left out and why
  const songs = data.songs;
  const rustyCount = songs.filter(s => s.rusty).length;
  const notReady = allSongs.filter(s => s.is_active && !usedSongIds.has(s.id) && s.readiness === 'not_ready').length;
  const onSetList = allSongs.filter(s => s.is_active && usedSongIds.has(s.id)).length;
  const leftOut = [
    onSetList && `${onSetList} on the set list`,
    notReady && `${notReady} not ready`,
    data.effective_mode === 'curated' && candidates.filter(s => s.readiness === 'rusty').length && `${candidates.filter(s => s.readiness === 'rusty').length} rusty`,
    handpicking && candidates.length - songs.length > 0 && `${candidates.length - songs.length} not picked`,
  ].filter(Boolean);

  const q = search.trim().toLowerCase();
  const shown = candidates.filter(s =>
    (!genre || s.genre === genre) &&
    (!q || s.title.toLowerCase().includes(q) || (s.artist_original || '').toLowerCase().includes(q)));

  const chip = (active) => ({
    flex: 1, padding: isMobile ? '0.6rem 0.4rem' : '0.55rem 0.75rem', borderRadius: '8px', cursor: canEdit ? 'pointer' : 'default',
    border: `1.5px solid ${active ? theme.primary : theme.border}`,
    background: active ? theme.primary : theme.surface, color: active ? theme.onPrimary : theme.text,
    fontWeight: active ? '700' : '500', fontSize: '0.88rem',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.3rem',
  });
  const smallBtn = { padding: '0.35rem 0.65rem', background: theme.surfaceAlt, color: theme.text, border: `1px solid ${theme.border}`, borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '600' };
  const current = MODES.find(m => m.key === data.mode);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      {offline && (
        <div style={{ fontSize: '0.8rem', color: theme.textMuted }}>No signal: showing the saved request list. Changes need signal.</div>
      )}

      {/* Mode */}
      <div style={{ display: 'flex', gap: '0.4rem' }} role="radiogroup" aria-label="Request list mode">
        {MODES.map(m => {
          const locked = m.paid && !paid;
          return (
            <button key={m.key} role="radio" aria-checked={data.mode === m.key}
              disabled={!canEdit || saving || offline}
              onClick={() => chooseMode(m.key)}
              style={{ ...chip(data.mode === m.key), opacity: locked ? 0.55 : 1 }}>
              {locked && <Lock size={12} />}{m.label}
            </button>
          );
        })}
      </div>
      <div style={{ fontSize: '0.85rem', color: theme.textSecondary }}>
        {current?.blurb}
        {data.mode !== data.effective_mode && <> Hand-picked needs Pro or Band, so this gig uses <b>Curated</b> for now.</>}
        {data.carried_from && (
          <div style={{ marginTop: '0.25rem', color: theme.textMuted, fontSize: '0.8rem' }}>
            Same as your last gig here ({data.carried_from.gig_name}).
          </div>
        )}
      </div>

      {/* Live count */}
      <div style={{ padding: '0.65rem 0.8rem', background: `${theme.primary}12`, borderRadius: '8px' }}>
        <div style={{ fontWeight: '700', fontSize: '0.95rem' }}>
          Audience will see {songs.length} song{songs.length !== 1 ? 's' : ''}{rustyCount > 0 && ` (${rustyCount} rusty)`}
        </div>
        {data.capped && (
          <div style={{ fontSize: '0.8rem', color: theme.textSecondary, marginTop: '0.2rem' }}>
            The free plan shows the first {data.cap} of {data.total}.
          </div>
        )}
        {leftOut.length > 0 && (
          <div style={{ fontSize: '0.8rem', color: theme.textMuted, marginTop: '0.2rem' }}>Left out: {leftOut.join(' · ')}</div>
        )}
        {songs.length === 0 && (
          <div style={{ fontSize: '0.8rem', color: '#c53030', marginTop: '0.2rem' }}>The audience has nothing to request.</div>
        )}
      </div>

      {/* Print the tear-off sheet: only when there's a request block to use it in */}
      {hasRequestBlock && (
        <a href={`/print/gig/${gig.unique_url}/request-sheet`} target="_blank" rel="noreferrer"
          style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.65rem 0.8rem', borderRadius: '8px', border: `1px solid ${theme.border}`, background: theme.surface, color: theme.text, textDecoration: 'none' }}>
          <Printer size={18} color={theme.primary} style={{ flexShrink: 0 }} />
          <span style={{ flex: 1 }}>
            <span style={{ fontWeight: '600', fontSize: '0.9rem', display: 'block' }}>Print request sheet</span>
            <span style={{ fontSize: '0.78rem', color: theme.textMuted }}>Tear-off paper requests for the hat</span>
          </span>
        </a>
      )}

      {/* Hand-pick checklist, or a read-only preview */}
      {handpicking && canEdit ? (
        <div style={{ border: `1px solid ${theme.border}`, borderRadius: '8px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '0.6rem', display: 'flex', flexDirection: 'column', gap: '0.5rem', background: theme.surface, borderBottom: `1px solid ${theme.border}` }}>
            <div style={{ display: 'flex', gap: '0.4rem' }}>
              <input type="text" placeholder="Search songs…" value={search} onChange={e => setSearch(e.target.value)}
                style={{ flex: 1, minWidth: 0, padding: '0.5rem 0.65rem', border: `1px solid ${theme.border}`, borderRadius: '6px', background: theme.surface, color: theme.text, fontSize: '0.9rem' }} />
              {genres.length > 1 && (
                <select value={genre} onChange={e => setGenre(e.target.value)} aria-label="Genre"
                  style={{ maxWidth: '40%', padding: '0.5rem', border: `1px solid ${theme.border}`, borderRadius: '6px', background: theme.surface, color: theme.text, fontSize: '0.85rem' }}>
                  <option value="">All genres</option>
                  {genres.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              )}
            </div>
            <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <button style={smallBtn} onClick={() => setDraft(new Set(candidates.filter(s => s.readiness === 'ready').map(s => s.id)))}>Select all gig-ready</button>
              <button style={smallBtn} onClick={() => setDraft(new Set())}>Clear</button>
              <span style={{ fontSize: '0.8rem', color: theme.textMuted, marginLeft: 'auto' }}>
                {candidates.filter(s => picked.has(s.id)).length} picked
              </span>
            </div>
          </div>
          <div style={{ maxHeight: isMobile ? 'none' : '40vh', overflowY: 'auto' }}>
            {shown.length === 0 && <div style={{ padding: '1.5rem', textAlign: 'center', color: theme.textMuted, fontSize: '0.9rem' }}>No songs match.</div>}
            {shown.map(s => {
              const on = picked.has(s.id);
              return (
                <div key={s.id} role="checkbox" aria-checked={on} tabIndex={0}
                  onClick={() => togglePick(s.id)}
                  onKeyDown={e => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); togglePick(s.id); } }}
                  style={{ padding: '0.55rem 0.75rem', borderBottom: `1px solid ${theme.border}`, display: 'flex', alignItems: 'center', gap: '0.65rem', cursor: 'pointer', background: on ? `${theme.primary}10` : theme.surface }}>
                  <div style={{ width: 20, height: 20, borderRadius: '4px', flexShrink: 0, border: `2px solid ${on ? theme.primary : theme.border}`, background: on ? theme.primary : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {on && <Check size={12} color={theme.onPrimary} />}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.9rem', fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {s.title}{s.readiness === 'rusty' && <span title="Rusty" style={{ marginLeft: '0.35rem' }}>🤞</span>}
                    </div>
                    <div style={{ fontSize: '0.78rem', color: theme.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {s.artist_original}{s.genre && ` · ${s.genre}`}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {draft && (
            <div style={{ padding: '0.6rem', display: 'flex', gap: '0.5rem', background: theme.surface, borderTop: `1px solid ${theme.border}` }}>
              <button disabled={saving || offline} onClick={() => save({ mode: 'handpicked', picks: [...draft] }, 'Picks saved')}
                style={{ flex: 1, padding: '0.7rem', background: theme.primary, color: theme.onPrimary, border: 'none', borderRadius: '8px', fontWeight: '700', cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
                {saving ? 'Saving…' : `Save picks (${candidates.filter(s => draft.has(s.id)).length})`}
              </button>
              <button onClick={() => setDraft(null)} style={{ ...smallBtn, padding: '0.7rem 0.85rem' }}>Undo changes</button>
            </div>
          )}
        </div>
      ) : songs.length > 0 && (
        <div>
          <button onClick={() => setShowList(v => !v)}
            style={{ background: 'none', border: 'none', padding: 0, color: theme.primary, cursor: 'pointer', fontWeight: '600', fontSize: '0.88rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
            {showList ? <ChevronUp size={15} /> : <ChevronDown size={15} />} {showList ? 'Hide' : 'Show'} the songs
          </button>
          {showList && (
            <div style={{ marginTop: '0.5rem', border: `1px solid ${theme.border}`, borderRadius: '8px', maxHeight: isMobile ? 'none' : '40vh', overflowY: 'auto' }}>
              {songs.map(s => (
                <div key={s.id} style={{ padding: '0.45rem 0.75rem', borderBottom: `1px solid ${theme.border}`, fontSize: '0.88rem' }}>
                  {s.title}{s.rusty && <span title="Rusty" style={{ marginLeft: '0.35rem' }}>🤞</span>}
                  <span style={{ color: theme.textMuted, fontSize: '0.78rem' }}> · {s.artist_original}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default RequestListPanel;
